import React, { useState } from 'react';
import { Button, Input, message, Space, Tooltip, Alert } from 'antd';
import { CopyOutlined, LinkOutlined, ExportOutlined } from '@ant-design/icons';
import fineoPayService from '../../services/fineoPayService';

interface PaymentLinkGeneratorProps {
  orderId: number;
  amount: number;
  reference: string;
  onSuccess?: (paymentUrl: string) => void;
}

const PaymentLinkGenerator: React.FC<PaymentLinkGeneratorProps> = ({
  orderId,
  amount,
  reference,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);

  const handleGenerateLink = async () => {
    setLoading(true);

    try {
      const result = await fineoPayService.generatePaymentLink(
        orderId,
        amount,
        `Commande ${reference}`,
        `Paiement pour la commande ${reference}`
      );

      if (result.success && result.data.paymentUrl) {
        setPaymentUrl(result.data.paymentUrl);
        message.success('Lien de paiement généré avec succès !');
        
        if (onSuccess) {
          onSuccess(result.data.paymentUrl);
        }
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Erreur lors de la génération du lien');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (paymentUrl) {
      navigator.clipboard.writeText(paymentUrl);
      message.success('Lien copié dans le presse-papiers !');
    }
  };

  const handleOpenLink = () => {
    if (paymentUrl) {
      window.open(paymentUrl, '_blank', 'width=800,height=600');
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      {!paymentUrl ? (
        <Button
          type="primary"
          icon={<LinkOutlined />}
          onClick={handleGenerateLink}
          loading={loading}
          block
        >
          Générer un lien de paiement FineoPay
        </Button>
      ) : (
        <div>
          <Alert
            message="Lien de paiement généré avec succès !"
            description="Copiez ce lien et envoyez-le au client pour qu'il effectue le paiement."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Space.Compact style={{ width: '100%' }}>
            <Input value={paymentUrl} readOnly />
            
            <Tooltip title="Copier le lien">
              <Button icon={<CopyOutlined />} onClick={handleCopyLink} />
            </Tooltip>
            
            <Tooltip title="Ouvrir dans une nouvelle fenêtre">
              <Button icon={<ExportOutlined />} onClick={handleOpenLink} />
            </Tooltip>
          </Space.Compact>

          <Button
            style={{ marginTop: 16, width: '100%' }}
            onClick={() => setPaymentUrl(null)}
          >
            Générer un nouveau lien
          </Button>
        </div>
      )}
    </div>
  );
};

export default PaymentLinkGenerator;
